/**
 * "Hozir qo'ng'iroq qilsa bo'ladi": doiradagi faol driverlar ichida mahalliy vaqti qo'ng'iroq oynasida bo'lganlar --
 * eslatmasi kelganlar birinchi, keyin eng uzoq gaplashilmaganlar. Backend nomzodlarni beradi, oyna har daqiqa shu yerda
 * hisoblanadi (shtat -> zona). Bosilsa driver profili.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { PhoneCall } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNow } from "@/lib/useNow";
import { tzAbbr } from "@/features/drivers/timezones";
import type { Me } from "@/features/auth/types";
import type { WidgetItem } from "../types";
import { useWidgetData } from "../data";
import { autoHour12, clockText, zoneParts } from "../clock/time";
import { BigNumber, WidgetEmpty, WidgetRow } from "../ui";
import type { CallNowConfig } from "./config";
import { countCallable, daysSince, pickCallable, type CallCandidate, type Callable } from "./pick";

type Props = { item: WidgetItem<CallNowConfig>; me: Me };
type Data = { drivers: CallCandidate[]; states: Record<string, number>; total: number };
const ENTITIES = ["driver", "reminder"];

function useCallable(config: CallNowConfig) {
  const now = useNow("minute");
  const q = useWidgetData<Data>("callnow", {}, ENTITIES);
  const call = config.call;
  const list = useMemo(() => pickCallable(q.data?.drivers ?? [], now, call), [q.data, now, call]);
  // Son -- ro'yxatdan emas, shtatlar sonidan (ro'yxat har shtatdan 20 tagacha)
  const count = useMemo(() => (q.data?.states ? countCallable(q.data.states, now, call) : list.length), [q.data, now, call, list.length]);
  return { list, count, now, loading: q.isPending, failed: q.isError };
}

function Row({ d, now, hour12 }: { d: Callable; now: Date; hour12: boolean }) {
  const { t } = useTranslation("widgets");
  const navigate = useNavigate();
  const { time, suffix } = clockText(zoneParts(now, d.tz), hour12);
  const since = daysSince(d.last_contact_at, now);
  return (
    <WidgetRow
      title={d.name}
      sub={`${d.state} ${time}${suffix ? ` ${suffix}` : ""} ${tzAbbr(d.tz, now)} · ${d.status_label}`}
      dot={d.reminder_due ? "bg-primary" : undefined}
      right={d.reminder_due ? t("callnow.reminder") : since === null ? t("callnow.never") : t("callnow.daysAgo", { count: since })}
      onClick={() => navigate(`/drivers/${d.driver_id}`)}
    />
  );
}

export function CallNowView({ item }: Props) {
  const { t, i18n } = useTranslation("widgets");
  const { list, count, now, loading, failed } = useCallable(item.config);
  const hour12 = autoHour12(i18n.resolvedLanguage ?? i18n.language);
  const empty = !loading && count === 0;
  const emptyText = t(failed ? "failed" : "callnow.empty");

  if (item.size === "s") {
    return (
      <div className="flex h-full flex-col px-3 py-2.5">
        <BigNumber value={loading ? "–" : count} label={t("callnow.now")} tone={count > 0 ? undefined : "text-muted"} />
        <div className="mt-auto min-w-0">
          {list[0] ? <div className="truncate text-[12px] font-medium">{list[0].name}</div> : empty && <div className="text-[11px] text-muted">{emptyText}</div>}
          {list[0] && <div className="truncate text-[11px] text-muted">{list[0].state} · {clockText(zoneParts(now, list[0].tz), hour12).time}</div>}
        </div>
      </div>
    );
  }
  const shown = list.slice(0, item.size === "m" ? 3 : item.config.limit);
  return (
    <div className="flex h-full flex-col px-3 pt-2 pb-1.5">
      <div className="mb-0.5 flex items-baseline gap-1.5 px-1.5">
        <span className={cn("text-[22px] leading-none font-semibold tracking-[-0.02em] tabular-nums", count === 0 && "text-muted")}>{loading ? "–" : count}</span>
        <span className="truncate text-[11px] text-muted">{t("callnow.now")}</span>
      </div>
      <div className={cn("min-h-0 flex-1", item.size === "m" ? "overflow-hidden" : "overflow-y-auto")}>
        {shown.length ? shown.map((d) => <Row key={d.driver_id} d={d} now={now} hour12={hour12} />) : empty && <WidgetEmpty text={emptyText} />}
      </div>
    </div>
  );
}

export function CallNowGlance({ item }: Props) {
  const { t } = useTranslation("widgets");
  const { count } = useCallable(item.config);
  return (
    <span className="inline-flex items-center gap-1.5" title={t("callnow.title")}>
      <PhoneCall size={13} strokeWidth={1.75} className="text-muted" aria-hidden />
      <span className={cn("text-[12px] font-semibold tabular-nums", count === 0 && "text-muted")}>{count}</span>
    </span>
  );
}
